import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider, type Persister } from '@tanstack/react-query-persist-client';
import type { Outbox, ZibanuClient } from '@zibanu/client';
import { devicePersistOptions } from './cache';
import type { InstallStore } from './install';
import { loginFlow } from './loginFlow';
import { NoticesProvider } from './notices';
import { BooksProvider } from './session';

export interface Platform {
  deviceLabel(): string;
  openWhatsApp(): void;
  now(): Date;
  install: InstallStore;
}

export interface AppDeps {
  client: ZibanuClient;
  outbox: Outbox;
  queryClient: QueryClient;
  platform: Platform;
  /** The read cache on the phone; tests leave it out. */
  persister?: Persister | undefined;
}

const DepsContext = createContext<AppDeps | null>(null);

export function useDeps(): AppDeps {
  const deps = useContext(DepsContext);
  if (!deps) throw new Error('useDeps outside AppProviders');
  return deps;
}

/** Everything the screens reach for. A session that ends anywhere clears who you are (spec 3A §6.2). */
export function AppProviders({ deps, children }: { deps: AppDeps; children: ReactNode }) {
  useSignedOut(deps);
  const inner = (
    <BooksProvider>
      <NoticesProvider>{children}</NoticesProvider>
    </BooksProvider>
  );
  return (
    <DepsContext.Provider value={deps}>
      {deps.persister ? (
        // Restores the saved copy before any read runs (spec 3A §6.4).
        <PersistQueryClientProvider client={deps.queryClient} persistOptions={devicePersistOptions(deps.persister)}>
          {inner}
        </PersistQueryClientProvider>
      ) : (
        <QueryClientProvider client={deps.queryClient}>{inner}</QueryClientProvider>
      )}
    </DepsContext.Provider>
  );
}

/** Any call answered "unauthenticated": who you are is cleared, and if you were signed in until now, login says so (spec 3A §9). */
function useSignedOut(deps: AppDeps): void {
  useEffect(
    () =>
      deps.client.onSignedOut(() => {
        if (deps.queryClient.getQueryData(['me'])) loginFlow.markEnded();
        deps.queryClient.setQueryData(['me'], null);
      }),
    [deps],
  );
}
